import React, { useState } from 'react';
import {
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import { ITechnician } from '../../../utils/entities/interface';
import { capitalizeWords, formatDate } from '../../../utils/helper';
import { useNavigate } from 'react-router-dom';
import { RemoveRedEyeOutlined } from '@mui/icons-material';
import { ToggleTechnicianStatus } from '../../../services/technician';
import { ModalConfirmation } from '../../molecules/ModalConfirmation';

const ColGroup = ({ headerCells, showCheckbox }: { headerCells: [string, number][]; showCheckbox: boolean }) => {
  const totalWidth = headerCells.reduce((sum, [_, w]) => sum + w, 0);
  return (
    <colgroup>
      {showCheckbox && <col style={{ width: '40px', minWidth: '40px', maxWidth: '40px' }} />}
      {headerCells.map(([_, width], i) => (
        <col key={i} style={{ width: `${(width / totalWidth) * 100}%` }} />
      ))}
    </colgroup>
  );
};

export const TechnicianList = ({
  technicians,
  isLoading,
  onUpdate,
  headerCells,
  showCheckbox = false,
  selectedIds = [],
  onSelectionChange,
}: {
  technicians: ITechnician[];
  headerCells: [string, number][];
  isLoading: boolean;
  onUpdate: () => void;
  showCheckbox?: boolean;
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
}) => {
  const [openModal, setOpenModal] = useState<boolean>(false);
  const navigate = useNavigate();
  const [selectedTechnician, setSelectedTechnician] = useState<ITechnician | null>(null);

  const handleClose = () => {
    setOpenModal(false);
    setSelectedTechnician(null);
  };

  const handleOpenModal = (technician: ITechnician) => {
    setSelectedTechnician(technician);
    setOpenModal(true);
  };

  const handleConfirmToggle = async () => {
    if (!selectedTechnician) return;
    await ToggleTechnicianStatus(selectedTechnician._id);
    onUpdate();
    handleCloseModal();
  };

  const handleCloseModal = () => {
    setSelectedTechnician(null);
    setOpenModal(false);
  };

  const handleSelectAll = (checked: boolean) => {
    if (!onSelectionChange) return;
    if (checked) {
      onSelectionChange(technicians.map((t) => t._id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    if (!onSelectionChange) return;
    if (checked) {
      onSelectionChange([...selectedIds, id]);
    } else {
      onSelectionChange(selectedIds.filter((i) => i !== id));
    }
  };

  const isAllSelected = technicians.length > 0 && selectedIds.length === technicians.length;
  const isIndeterminate = selectedIds.length > 0 && selectedIds.length < technicians.length;

  const renderCell = (technician: ITechnician, colIndex: number) => {
    switch (colIndex) {
      case 0:
        return capitalizeWords(technician.name) || 'N/A';
      case 1:
        return '+91-' + technician.phoneNumber.slice(0, 10);
      case 2:
        return formatDate(technician.dob) || 'N/A';
      case 3:
        return technician.type || 'N/A';
      case 4:
        return technician.experience || 'N/A';
      case 5:
        return capitalizeWords(technician.position);
      case 6:
        return (
          <Button
            variant="outlined"
            size="small"
            color={
              technician.status == 'AVAILABLE'
                ? 'error'
                : technician.status == 'KYC_PENDING'
                  ? 'warning'
                  : 'success'
            }
            onClick={() => {
              if (technician.status !== 'KYC_PENDING') handleOpenModal(technician);
            }}
            sx={{ textTransform: 'none', fontSize: '0.8rem', borderRadius: '8px' }}
          >
            {technician.status == 'AVAILABLE'
              ? 'Disable'
              : technician.status == 'KYC_PENDING'
                ? 'Pending'
                : 'Enable'}
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <TableContainer
        sx={{
          overflow: 'hidden',
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Table sx={{ tableLayout: 'fixed', width: '100%', borderCollapse: 'separate', borderSpacing: '0 5px' }}>
            <ColGroup headerCells={headerCells} showCheckbox={showCheckbox} />
            <TableHead>
              <TableRow>
                {showCheckbox && (
                  <TableCell
                    sx={{
                      textAlign: 'center',
                      color: '#FFFFFF',
                      backgroundColor: '#1C1C1C',
                      padding: '16px 4px',
                      borderTop: '1px solid #8A8D91',
                      borderBottom: '1px solid #8A8D91',
                      borderLeft: '1px solid #8A8D91',
                      borderTopLeftRadius: 10,
                      borderBottomLeftRadius: 10,
                      width: '40px',
                      minWidth: '40px',
                      maxWidth: '40px',
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={isAllSelected}
                      indeterminate={isIndeterminate}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      sx={{ color: '#8A8D91', '&.Mui-checked': { color: '#E31E25' }, '&.MuiCheckbox-indeterminate': { color: '#E31E25' }, p: 0 }}
                    />
                  </TableCell>
                )}
                {headerCells.map(([label], index) => (
                  <TableCell
                    key={index}
                    sx={{
                      textAlign: 'center',
                      color: '#FFFFFF',
                      backgroundColor: '#1C1C1C',
                      padding: '25px 8px',
                      borderTop: '1px solid #8A8D91',
                      borderBottom: '1px solid #8A8D91',
                      borderLeft: !showCheckbox && index === 0 ? '1px solid #8A8D91' : 'none',
                      borderRight: index === headerCells.length - 1 ? '1px solid #8A8D91' : 'none',
                      borderTopLeftRadius: !showCheckbox && index === 0 ? 10 : 0,
                      borderBottomLeftRadius: !showCheckbox && index === 0 ? 10 : 0,
                      borderTopRightRadius: index === headerCells.length - 1 ? 10 : 0,
                      borderBottomRightRadius: index === headerCells.length - 1 ? 10 : 0,
                    }}
                  >
                    {label === 'Action' ? 'Action' : label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          </Table>
        </Box>

        <Box data-scroll-body sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', scrollbarWidth: 'none' }}>
          <Table sx={{ tableLayout: 'fixed', width: '100%', borderCollapse: 'separate', borderSpacing: '0 8px' }}>
            <ColGroup headerCells={headerCells} showCheckbox={showCheckbox} />
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={headerCells.length + (showCheckbox ? 1 : 0)} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : technicians.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={headerCells.length + (showCheckbox ? 1 : 0)} align="center">
                    No data found
                  </TableCell>
                </TableRow>
              ) : (
                technicians.map((technician, rowIndex) => (
                  <TableRow
                    key={rowIndex}
                    sx={{
                      backgroundColor: '#1C1C1C',
                      '& > td': {
                        borderTop: '1px solid #3E3E3E',
                        borderBottom: '1px solid #3E3E3E',
                        color: '#8A8D91',
                        paddingY: '10px',
                        textAlign: 'center',
                        backgroundColor: '#1C1C1C',
                      },
                      '& > td:first-of-type': {
                        borderLeft: '1px solid #3E3E3E',
                        borderTopLeftRadius: 12,
                        borderBottomLeftRadius: 12,
                      },
                      '& > td:last-of-type': {
                        borderRight: '1px solid #3E3E3E',
                        borderTopRightRadius: 12,
                        borderBottomRightRadius: 12,
                      },
                    }}
                  >
                    {showCheckbox && (
                      <TableCell sx={{ textAlign: 'center', padding: '8px 4px', width: '40px', minWidth: '40px', maxWidth: '40px' }}>
                        <Checkbox
                          size="small"
                          checked={selectedIds.includes(technician._id)}
                          onChange={(e) => handleSelectOne(technician._id, e.target.checked)}
                          sx={{ color: '#8A8D91', '&.Mui-checked': { color: '#E31E25' }, p: 0 }}
                        />
                      </TableCell>
                    )}
                    {headerCells.map(([label], colIdx) => (
                      <TableCell key={colIdx} sx={{ textAlign: 'center', padding: '10px 8px' }}>
                        {label === 'Action' ? (
                          <IconButton
                            sx={{ color: '#E31E25' }}
                            onClick={() => navigate(`/technicians/${technician._id}`)}
                          >
                            <RemoveRedEyeOutlined />
                          </IconButton>
                        ) : (
                          renderCell(technician, colIdx)
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </TableContainer>

      <ModalConfirmation
        open={openModal}
        handleClose={handleClose}
        handleConfirm={handleConfirmToggle}
        selectedItem={selectedTechnician}
        entityName="technician"
      />
    </>
  );
};
